import { Button, ButtonVariant, Modal, ModalSize, Spinner, SpinnerSize, Text, TextArea, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import type { JSX } from "react";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

interface IEditImageModalProps {
  open: boolean;
  instruction: string;
  isGenerating: boolean;
  referenceImageUrl?: string;
  errorMessage?: string | null;
  onInstructionChange: (value: string) => void;
  onCancel: () => void;
  onGenerate: () => void;
  onCancelGeneration?: () => void;
}

export function EditImageModal({
  open,
  instruction,
  isGenerating,
  referenceImageUrl,
  errorMessage,
  onInstructionChange,
  onCancel,
  onGenerate,
  onCancelGeneration,
}: IEditImageModalProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal open={open} onOpenChange={(isOpen: boolean) => !isOpen && onCancel()} size={ModalSize.MEDIUM}>
      <Col className="gap-2">
        <Text variant={TextVariants.HEADING_6_BOLD}>
          <Trans i18nKey="common:modal.generateAvatar.editImage.title" />
        </Text>

        {referenceImageUrl ? (
          <Col className="gap-2">
            <div className="p-0 rounded-md inline-block">
              <img
                src={referenceImageUrl}
                alt={t("common:modal.generateAvatar.imageAlt.referenceImage")}
                className="w-28 h-42 object-cover rounded-md"
              />
            </div>
          </Col>
        ) : null}

        <Col className="mb-0.5">
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="ml-[2px]">
            <Trans i18nKey="common:modal.generateAvatar.editImage.instructionsLabel" />
          </Text>
          <TextArea value={instruction} onChange={onInstructionChange} placeholder={t("common:modal.generateAvatar.editImage.placeholder")} />
        </Col>

        {errorMessage ? <Text className="text-center text-error">{errorMessage}</Text> : null}

        <Row justify="end" className="gap-2">
          {isGenerating ? (
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={onCancelGeneration}
              className="border-solid border-1 border-gray-11 flex items-center gap-2"
            >
              <Spinner size={SpinnerSize.SMALL} />
              <Trans i18nKey="common:modal.generateAvatar.cta.stopGeneration" />
            </Button>
          ) : (
            <>
              <Button variant={ButtonVariant.OUTLINED} onClick={onCancel}>
                <Trans i18nKey="common:cancel" />
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={onGenerate} disabled={!instruction.trim()}>
                <Trans i18nKey="common:modal.generateAvatar.editImage.cta.generate" />
              </Button>
            </>
          )}
        </Row>
      </Col>
    </Modal>
  );
}
